import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useCategories from '../hooks/useCategories';
import { useTranslation } from '../hooks/useTranslation';
import { capitalizeFirstLetter } from '../utils/textUtils';

interface CategorySelectorProps {
  onCategorySelect?: (category: string) => void;
  selectedCategory?: string;
  categories?: string[];
}

const CategorySelector: React.FC<CategorySelectorProps> = ({
  onCategorySelect,
  selectedCategory,
  categories: propCategories
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { categories: apiCategories } = useCategories();
  const { t } = useTranslation();

  // Usa le categorie dall'API o quelle fornite come props
  const categories = propCategories || apiCategories;  const handleCategorySelect = (category: string) => {
    setIsOpen(false);
    
    if (onCategorySelect) {
      onCategorySelect(category);
    } else {
      // Naviga alla pagina listings con filtro categoria
      navigate(`/escorts?category=${encodeURIComponent(category)}`);
    }
  };

  const handleShowAll = () => {
    setIsOpen(false);
    navigate('/escorts');
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center hover:text-pink-200 transition-colors duration-200"
      >
        <span>{t('categorySelector.categoriesLabel')}</span>
        <ChevronDown 
          className={`ml-1 h-4 w-4 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {isOpen && (
        <>
          {/* Overlay to close dropdown */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown menu */}
          <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
            <div className="py-2">
              {/* All Escorts option */}
              <button
                onClick={handleShowAll}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-pink-50 hover:text-pink-600 transition-colors ${
                  !selectedCategory ? 'bg-pink-50 text-pink-600 font-medium' : 'text-gray-700'
                }`}
              >
                📋 {t('common.tutte_le_escort')}
              </button>
              
              {/* Separator */}
              <div className="border-t border-gray-100 my-1" />
                {/* Category list */}
              {categories.map((category, index) => (
                <button
                  key={index}
                  onClick={() => handleCategorySelect(category)}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-pink-50 hover:text-pink-600 transition-colors ${
                    selectedCategory === category ? 'bg-pink-50 text-pink-600 font-medium' : 'text-gray-700'
                  }`}
                >
                  {getCategoryIcon(category, t)} {capitalizeFirstLetter(category)}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Helper function to get category icon
const getCategoryIcon = (category: string, t: any): string => {
  // Try to get icon from translations first
  const iconKey = `categorySelector.categoryIcons.${category}`;
  const translatedIcon = t(iconKey);
  
  // If translation exists and is not the same as the key, use it
  if (translatedIcon && translatedIcon !== iconKey) {
    return translatedIcon;
  }
  
  // Fallback to default icon
  return t('categorySelector.categoryIcons.default');
};

export default CategorySelector;
